import { Button, Select, TextField } from '@plaidev/sour-react';
import type { ReactElement } from 'react';

import { useColumnEditor } from '../context/ColumnEditorContext';
import type { ColumnDraft } from '../types';
import { ColumnSpreadsheet } from './ColumnSpreadsheet';

export function ConfigureColumnMappingSection(): ReactElement {
  const {
    counts,
    searchText,
    setSearchText,
    destinationFilter,
    setDestinationFilter,
    selectedNames,
    clearColumnSelection,
    bulkSetDestination,
    filteredColumns,
    contractColumnNames,
  } = useColumnEditor();

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-default">列の振り分け設定</h2>
          <p className="text-xs text-muted-light">1 行 = 1 列です。振り分け先・型・整形・一意キーをこの表で一括設定できます。</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-primary-light px-2 py-0.5 text-xs text-primary">
            顧客 {counts.customer}列
          </span>
          <span className="inline-flex items-center rounded-full bg-notice-light px-2 py-0.5 text-xs text-notice">
            契約 {counts.contract}列
          </span>
          <span className="inline-flex items-center rounded-full bg-subtle px-2 py-0.5 text-xs text-muted-light">
            無視 {counts.ignore}列
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[220px]">
          <TextField variant="contained" placeholder="列名で検索" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </div>
        <div className="w-[180px]">
          <Select
            variant="contained"
            searchable={false}
            value={destinationFilter}
            onChange={(v) => {
              if (typeof v === 'string') setDestinationFilter(v as 'all' | ColumnDraft['destination']);
            }}
            options={[
              { value: 'all', label: `すべて (${counts.all})` },
              { value: 'customer', label: `顧客 (${counts.customer})` },
              { value: 'contract', label: `契約 (${counts.contract})` },
              { value: 'ignore', label: `無視 (${counts.ignore})` },
            ]}
          />
        </div>
        {selectedNames.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-default bg-subtle-light px-3 py-1.5 text-xs">
            <span className="text-default">{selectedNames.size} 列を選択中</span>
            <span className="text-muted-light">一括変更:</span>
            <Button
              type="button"
              variant="basic"
              size="sm"
              className="border-primary/40 text-primary"
              onClick={() => bulkSetDestination('customer')}
            >
              顧客へ
            </Button>
            <Button
              type="button"
              variant="basic"
              size="sm"
              className="border-notice/50 text-notice"
              onClick={() => bulkSetDestination('contract')}
            >
              契約へ
            </Button>
            <Button type="button" variant="basic" size="sm" onClick={() => bulkSetDestination('ignore')}>
              無視へ
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={clearColumnSelection}>
              解除
            </Button>
          </div>
        ) : null}
      </div>

      <ColumnSpreadsheet />
      {filteredColumns.length === 0 ? <p className="text-xs text-muted-light">条件に一致する列がありません。</p> : null}
      <p className="text-xs text-muted-light">
        ※「無視」行はグレーアウトで編集不可になります。一意キー（契約UK / 顧客UK）は下の領域で指定します。
      </p>
      {contractColumnNames.length === 0 ? (
        <p className="text-xs text-negative">契約にマップされている列がありません。少なくとも 1 列を「契約」にしてください。</p>
      ) : null}
    </section>
  );
}
